import { useState, useRef, type FormEvent } from 'react'
import { Head } from '@inertiajs/react'
import {
  Gem,
  Monitor,
  Globe,
  Database,
  Phone,
  Mail,
  ChevronUp,
  Check,
  ArrowRight,
  Menu,
  X,
} from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Props {
  locale: string
  otherLocale: string
  otherLocaleName: string
  urls: {
    signIn: string
    signUp: string
    contactCreate: string
    localeToggle: string
  }
  translations: {
    home: string
    about: string
    caseStudies: string
    gallery: string
    contact: string
    signIn: string
    signUp: string
    heroTitle: string
    heroSubtitle: string
    learnMore: string
    weGotWhatYouNeed: string
    aboutDescription: string
    step1Title: string
    step1Desc1: string
    step1Desc2: string
    step2Title: string
    step2Desc1: string
    step2Desc2: string
    step3Title: string
    step3Desc1: string
    step3Desc2: string
    atYourService: string
    multipleTemplates: string
    multipleTemplatesDesc: string
    onSiteServices: string
    onSiteServicesDesc: string
    readyToPublish: string
    readyToPublishDesc: string
    controlYourData: string
    controlYourDataDesc: string
    iccaTitle: string
    iccaDesc: string
    iccaDetail: string
    lcgpaTitle: string
    lcgpaDesc: string
    lcgpaDetail: string
    packagesPricing: string
    platformFees: string
    landingPage: string
    upTo: string
    registration: string
    autoBadge: string
    autoQr: string
    attendanceReports: string
    additionalOnsiteServices: string
    printing: string
    perBadge: string
    serviceFee: string
    perDay: string
    registrationDesk: string
    unlimitedRegistrations: string
    negotiable: string
    getNow: string
    getInTouch: string
    getInTouchDesc: string
    name: string
    email: string
    message: string
    sendUsAMessage: string
    send: string
  }
}

/* ------------------------------------------------------------------ */
/*  Pricing tiers                                                      */
/* ------------------------------------------------------------------ */

const pricingTiers = [
  {
    range: '1-100',
    platform: 500,
    limit: 100,
    printing: 14,
    dailyCost: 1000,
    desks: '1',
    bg: 'from-[#2b62a4] to-[#3a7bc8]',
  },
  {
    range: '100-500',
    platform: 1750,
    limit: 500,
    printing: 12,
    dailyCost: 1300,
    desks: '3',
    bg: 'from-[#235086] to-[#2e68a8]',
  },
  {
    range: '500-1000',
    platform: 2700,
    limit: 1000,
    printing: 10,
    dailyCost: 1750,
    desks: '3',
    bg: 'from-[#1d4473] to-[#265995]',
  },
  {
    range: 'Unlimited',
    platform: 4500,
    limit: null,
    printing: 8,
    dailyCost: null,
    desks: '10',
    bg: 'from-[#252854] to-[#333678]',
  },
]

/* ------------------------------------------------------------------ */
/*  Services                                                           */
/* ------------------------------------------------------------------ */

const serviceIcons = [Gem, Monitor, Globe, Database]

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function Index(props: Props) {
  const { locale, otherLocaleName, urls, translations: t } = props
  const isRtl = locale === 'ar'
  const [mobileMenu, setMobileMenu] = useState(false)
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '', nickname: '' })
  const [contactStatus, setContactStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const contactRef = useRef<HTMLDivElement>(null)

  const services = [
    { icon: serviceIcons[0], title: t.multipleTemplates, desc: t.multipleTemplatesDesc },
    { icon: serviceIcons[1], title: t.onSiteServices, desc: t.onSiteServicesDesc },
    { icon: serviceIcons[2], title: t.readyToPublish, desc: t.readyToPublishDesc },
    { icon: serviceIcons[3], title: t.controlYourData, desc: t.controlYourDataDesc },
  ]

  const scrollTo = (id: string) => {
    setMobileMenu(false)
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleContactSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (contactForm.nickname) return // honeypot

    setContactStatus('sending')
    try {
      const token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
      const res = await fetch(urls.contactCreate, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { 'X-CSRF-Token': token } : {}),
        },
        body: JSON.stringify({ contact: { name: contactForm.name, email: contactForm.email, message: contactForm.message, nickname: contactForm.nickname } }),
      })
      if (res.ok) {
        setContactStatus('sent')
        setContactForm({ name: '', email: '', message: '', nickname: '' })
        setTimeout(() => setContactStatus('idle'), 5000)
      } else {
        setContactStatus('error')
      }
    } catch {
      setContactStatus('error')
    }
  }

  return (
    <div className={`min-h-screen bg-white ${isRtl ? 'text-right' : 'text-left'}`} dir={isRtl ? 'rtl' : 'ltr'}>
      <Head title="Eventy" />

      {/* ---- Navbar ---- */}
      <header className="sticky top-0 z-50 border-b border-gray-100 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
          <a onClick={() => scrollTo('home')} className="cursor-pointer">
            <span className="text-xl font-bold text-[#252854]">Event</span>
            <span className="text-xl font-bold text-[#2b62a4]">X</span>
          </a>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-6 text-sm font-medium text-[#252854] md:flex">
            <a onClick={() => scrollTo('home')} className="cursor-pointer transition-colors hover:text-[#2b62a4]">
              {t.home}
            </a>
            <a onClick={() => scrollTo('about')} className="cursor-pointer transition-colors hover:text-[#2b62a4]">
              {t.about}
            </a>
            <a onClick={() => scrollTo('cases')} className="cursor-pointer transition-colors hover:text-[#2b62a4]">
              {t.caseStudies}
            </a>
            <a onClick={() => scrollTo('gallery')} className="cursor-pointer transition-colors hover:text-[#2b62a4]">
              {t.gallery}
            </a>
            <a onClick={() => scrollTo('contact')} className="cursor-pointer transition-colors hover:text-[#2b62a4]">
              {t.contact}
            </a>
            <a href={urls.localeToggle} className="transition-colors hover:text-[#2b62a4]">
              {otherLocaleName}
            </a>
            <a href={urls.signIn} className="transition-colors hover:text-[#2b62a4]">
              {t.signIn}
            </a>
          </nav>

          <div className="hidden md:block">
            <a
              onClick={() => scrollTo('packages')}
              className="cursor-pointer rounded-lg bg-[#252854] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#333678]"
            >
              {t.signUp}
            </a>
          </div>

          {/* Mobile menu toggle */}
          <button onClick={() => setMobileMenu(!mobileMenu)} className="md:hidden">
            {mobileMenu ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile nav */}
        {mobileMenu && (
          <div className="border-t border-gray-100 bg-white px-4 py-4 md:hidden">
            <nav className="flex flex-col gap-3 text-sm font-medium text-[#252854]">
              <a onClick={() => scrollTo('home')} className="cursor-pointer">{t.home}</a>
              <a onClick={() => scrollTo('about')} className="cursor-pointer">{t.about}</a>
              <a onClick={() => scrollTo('cases')} className="cursor-pointer">{t.caseStudies}</a>
              <a onClick={() => scrollTo('gallery')} className="cursor-pointer">{t.gallery}</a>
              <a onClick={() => scrollTo('contact')} className="cursor-pointer">{t.contact}</a>
              <a href={urls.localeToggle}>{otherLocaleName}</a>
              <a href={urls.signIn}>{t.signIn}</a>
              <a onClick={() => scrollTo('packages')} className="cursor-pointer rounded-lg bg-[#252854] px-4 py-2 text-center text-white">
                {t.signUp}
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* ---- Hero ---- */}
      <section
        id="home"
        className="relative overflow-hidden bg-gradient-to-br from-[#1d3b5e]/80 to-[#252854]/80 py-20 text-white sm:py-32"
      >
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-8 px-4 text-center sm:px-6 lg:flex-row lg:text-start">
          <div className="flex-1 space-y-6">
            <h1 className="text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">
              {t.heroTitle}
            </h1>
            <p className="text-lg text-white/80 sm:text-xl">{t.heroSubtitle}</p>
            <button
              onClick={() => scrollTo('about')}
              className="inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3 text-sm font-semibold text-[#252854] transition-colors hover:bg-gray-100"
            >
              {t.learnMore}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <div className="hidden flex-1 lg:flex lg:justify-center">
            <div className="h-64 w-80 rounded-2xl bg-white/10 backdrop-blur" />
          </div>
        </div>
      </section>

      {/* ---- About ---- */}
      <section id="about" className="bg-[#F7F7F7] py-20">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
          <h2 className="text-2xl font-bold text-[#252854] sm:text-3xl">{t.weGotWhatYouNeed}</h2>
          <p className="mx-auto mt-6 max-w-2xl text-[#707070]">{t.aboutDescription}</p>

          {/* 3-step flow */}
          <div className="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-3">
            {[
              { step: 1, title: t.step1Title, lines: [t.step1Desc1, t.step1Desc2] },
              { step: 2, title: t.step2Title, lines: [t.step2Desc1, t.step2Desc2] },
              { step: 3, title: t.step3Title, lines: [t.step3Desc1, t.step3Desc2] },
            ].map(({ step, title, lines }) => (
              <div key={step} className="space-y-2">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#252854] text-sm font-bold text-white">
                  {step}
                </div>
                <h3 className="font-semibold text-[#252854]">{title}</h3>
                {lines.map((line, i) => (
                  <p key={i} className="text-sm text-[#707070]">{line}</p>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---- Services ---- */}
      <section className="py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h2 className="text-center text-2xl font-bold text-[#252854] sm:text-3xl">
            {t.atYourService}
          </h2>
          <div className="mx-auto my-4 h-1 w-16 rounded bg-[#2b62a4]" />
          <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {services.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="space-y-3 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#F7F7F7]">
                  <Icon className="h-8 w-8 text-[#2b62a4]" />
                </div>
                <h3 className="font-semibold text-[#252854]">{title}</h3>
                <p className="text-sm text-[#707070]">{desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <a
              onClick={() => scrollTo('packages')}
              className="cursor-pointer inline-flex rounded-lg bg-[#252854] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[#333678]"
            >
              {t.signUp}
            </a>
          </div>
        </div>
      </section>

      {/* ---- Case Studies ---- */}
      <section id="cases" className="bg-[#F7F7F7] py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <h2 className="mb-10 text-center text-2xl font-bold text-[#252854] sm:text-3xl">
            {t.caseStudies}
          </h2>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            {[
              { title: t.iccaTitle, desc: t.iccaDesc, detail: t.iccaDetail },
              { title: t.lcgpaTitle, desc: t.lcgpaDesc, detail: t.lcgpaDetail },
            ].map((cs) => (
              <div
                key={cs.title}
                className="rounded-xl border border-gray-200 bg-white p-6 text-center shadow-sm"
              >
                <div className="mb-4 h-24 w-full rounded-lg bg-gray-100" />
                <h3 className="text-lg font-semibold text-[#252854]">{cs.title}</h3>
                <p className="mt-2 text-sm text-[#707070]">{cs.desc}</p>
                <p className="mt-1 text-sm text-[#707070]">{cs.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---- Packages / Pricing ---- */}
      <section id="packages" className="py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h2 className="mb-12 text-center text-2xl font-bold text-[#252854] sm:text-3xl">
            {t.packagesPricing}
          </h2>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 xl:grid-cols-4">
            {pricingTiers.map((tier) => (
              <div
                key={tier.range}
                className={`relative overflow-visible rounded-xl bg-gradient-to-b ${tier.bg} p-5 pt-0 text-white shadow-lg`}
              >
                <div className="-mt-5 mb-4 rounded-lg bg-white px-4 py-3 text-center shadow-md">
                  <span className="text-2xl font-bold text-gray-800">{tier.range}</span>
                </div>
                <h3 className="mb-3 text-center text-sm font-medium text-white/90">
                  {t.platformFees} {tier.platform} SAR
                </h3>
                <ul className="space-y-1.5 px-2 text-sm text-white/80">
                  <li className="flex items-start gap-2">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    {t.landingPage}
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    {tier.limit
                      ? `${t.upTo} ${tier.limit} ${t.registration}`
                      : t.unlimitedRegistrations}
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    {t.autoBadge}
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    {t.autoQr}
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    {t.attendanceReports}
                  </li>
                </ul>
                <h4 className="mt-4 mb-2 text-center text-sm font-medium text-white/90">
                  {t.additionalOnsiteServices}
                </h4>
                <ul className="space-y-1.5 px-2 text-sm text-white/80">
                  <li className="flex items-start gap-2">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    {t.printing} {tier.printing} SAR {t.perBadge}
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    {t.serviceFee} {tier.dailyCost ? `${tier.dailyCost} ${t.perDay}` : t.negotiable}
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    {t.upTo} {tier.desks} {t.registrationDesk}
                  </li>
                </ul>
                <div className="mt-5 text-center">
                  <a
                    href={urls.signUp}
                    className="inline-flex rounded-lg bg-white px-6 py-2.5 text-sm font-semibold text-gray-800 transition-colors hover:bg-gray-100"
                  >
                    {t.getNow}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---- Gallery ---- */}
      <section id="gallery" className="bg-[#F7F7F7] py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h2 className="mb-10 text-center text-2xl font-bold text-[#252854] sm:text-3xl">
            {t.gallery}
          </h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className={`${i === 1 ? 'col-span-2 row-span-2' : ''} overflow-hidden rounded-lg bg-gray-200`}
              >
                <div className={`${i === 1 ? 'h-64' : 'h-32'} w-full bg-gray-300`} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---- Contact ---- */}
      <section id="contact" className="py-20" ref={contactRef}>
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-[#252854] sm:text-3xl">{t.getInTouch}</h2>
            <div className="mx-auto my-4 h-1 w-16 rounded bg-[#2b62a4]" />
            <p className="text-[#707070]">{t.getInTouchDesc}</p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2">
            <div className="flex flex-col items-center gap-2 text-center">
              <Phone className="h-10 w-10 text-[#707070]" />
              <span dir="ltr" className="text-[#252854]">+966 11 472-9997</span>
            </div>
            <div className="flex flex-col items-center gap-2 text-center">
              <Mail className="h-10 w-10 text-[#707070]" />
              <a href="mailto:info@sccsaudi.com" className="text-[#2b62a4] hover:underline">
                info@sccsaudi.com
              </a>
            </div>
          </div>

          {/* Contact form */}
          <form onSubmit={handleContactSubmit} className="mt-10">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-[#252854]">{t.name}</label>
                <input
                  type="text"
                  required
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#2b62a4] focus:outline-none focus:ring-1 focus:ring-[#2b62a4]"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-[#252854]">{t.email}</label>
                <input
                  type="email"
                  required
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#2b62a4] focus:outline-none focus:ring-1 focus:ring-[#2b62a4]"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="mb-1 block text-sm font-medium text-[#252854]">{t.message}</label>
              <textarea
                rows={6}
                required
                placeholder={t.sendUsAMessage}
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#2b62a4] focus:outline-none focus:ring-1 focus:ring-[#2b62a4]"
              />
            </div>
            {/* Honeypot */}
            <div className="hidden">
              <input
                type="text"
                value={contactForm.nickname}
                onChange={(e) => setContactForm({ ...contactForm, nickname: e.target.value })}
              />
            </div>

            {contactStatus === 'sent' && (
              <div className="mt-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                Thank you for your message. We'll get back to you soon!
              </div>
            )}
            {contactStatus === 'error' && (
              <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                Message did not send. Please try again.
              </div>
            )}

            <div className="mt-4">
              <button
                type="submit"
                disabled={contactStatus === 'sending'}
                className="rounded-lg bg-[#252854] px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#333678] disabled:opacity-60"
              >
                {contactStatus === 'sending' ? '...' : t.send}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* ---- Footer ---- */}
      <footer className="bg-gray-50 py-8">
        <div className="text-center text-sm text-[#707070]">Copyright &copy; 2020 - SCC</div>
      </footer>

      {/* ---- Back to top ---- */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-6 right-6 z-40 rounded-full bg-[#252854] p-3 text-white shadow-lg transition-colors hover:bg-[#333678]"
        aria-label="Back to top"
      >
        <ChevronUp className="h-5 w-5" />
      </button>
    </div>
  )
}
